import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AttachmentHandler,
    DO_NOTHING,
    DateRange,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationFeatureInput,
    Lotam,
    NoticeAudience,
    NoticeType,
    NoticesService,
    Organisation,
    UpdateNoticeCommand,
    WorkTracker,
    defaultTimezone,
    fromTimestamp,
    observeFormControl,
    toTimestamp
} from '@flyfreely-portal-ui/flyfreely';
import { FeatureGroup } from '@flyfreely-portal-ui/map';
import { MODAL_OPTIONS } from '@flyfreely-portal-ui/ngx-bootstrap-customisation';
import { FeatureCollection } from 'geojson';
import { LinkAction } from 'libs/attachments/src/lib/link-manager/link-manager.interface';
import { noticeLocationsToFeatureCollection } from 'libs/missions/src/lib/helpers';
import { isNumber } from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    BehaviorSubject,
    Subject,
    combineLatest,
    firstValueFrom,
    throwError
} from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { getNoticeFeatureGroups } from '../helpers';
import { LotamDataService, formatLotamPriority } from '../lotam-data.service';
import { LotamLocationEditComponent } from '../lotam-location-edit/lotam-location-edit.component';
import {
    MissionNoticesDataService,
    SimplifiedLotamDetails
} from '../mission-notices-data.service';

@Component({
    selector: 'lotam-edit',
    templateUrl: './lotam-edit.component.html',
    styleUrls: ['./lotam-edit.component.scss'],
    providers: [MissionNoticesDataService]
})
export class LotamEditComponent implements OnInit, OnDestroy {
    @Input() lotam?: Lotam;
    @Input() organisation: Organisation;
    @Input() isNew: false | number | NoticeAudience = false;
    @Output() update = new EventEmitter<Lotam>();
    @Output() editingCancelled = new EventEmitter<void>();

    lotamId: number;
    attachmentsHandler: AttachmentHandler;

    lotamForm: FormGroup;
    /** Null means no location defined. Non-null means the user has committed to
     *  having a location (either loaded from server or just confirmed in the
     *  location editor). Feature groups always carry the correct category/style
     *  structure; only existingFeatures varies. */
    locationFeatures: FeatureGroup[] | null = null;
    locationChanged$ = new Subject<void>();
    mapData$ = new BehaviorSubject<{ [source: string]: FeatureCollection }>({});

    utcDateRangeString: string;
    timeZone: string;
    timeZoneLabel: string;

    noticeTypes: NoticeType[];

    workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    working = false;

    priorityOptions = Object.entries(formatLotamPriority).map(
        ([value, label]) => ({ label, value })
    );


    constructor(
        private lotamService: LotamDataService,
        private noticesService: NoticesService,
        private modalService: BsModalService,
        private missionNoticesDataService: MissionNoticesDataService,
        private commonDialoguesService: CommonDialoguesService,
        private logging: FlyFreelyLoggingService
    ) {
        // if (modalOptions && this.modal && this.isNew) {
        //     modalOptions.closeInterceptor = () => {
        //         if (this.lotamForm?.dirty) {
        //             return commonDialoguesService.showConfirmationDialogue(
        //                 'Cancel Create Lotam',
        //                 'You have unsaved changes, are you sure you want to cancel?',
        //                 'Yes',
        //                 () =>
        //                     firstValueFrom(
        //                         this.lotamService.deleteLotam(this.lotamId)
        //                     )
        //             );
        //         }
        //         return Promise.resolve();
        //     };
        // }
        combineLatest([this.lotamService.working$, this.workTracker.observable])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([serviceWorking, working]) => {
                this.working = working || serviceWorking;
            });
    }

    ngOnInit() {
        this.timeZone = this.organisation.timeZone ?? defaultTimezone;
        this.initialiseForm();

        this.setupFormValueChanges();
        this.updateLotamUtcDateRange();

        this.setupFormData();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapData$.complete();
        this.locationChanged$.complete();
    }

    private async setupFormData() {
        let audience;
        if (this.isNew === false) {
            audience = await this.initialiseForEdit();
        } else if (isNumber(this.isNew)) {
            audience = await this.loadLotamForClone(this.isNew);
        } else {
            audience = await this.createNewLotam(this.isNew);
        }
        this.refreshNoticeTypes(audience);
    }

    private initialiseForm() {
        this.lotamForm = new FormGroup({
            subject: new FormControl(
                this.isNew ? '' : this.lotam?.subject,
                Validators.required
            ),
            description: new FormControl(
                this.isNew ? '' : this.lotam?.description,
                Validators.required
            ),
            noticeType: new FormControl(
                this.isNew ? null : this.lotam?.noticeType
            ),
            startTime: new FormControl(
                this.isNew
                    ? null
                    : fromTimestamp(this.lotam?.startTime, this.timeZone),
                Validators.required
            ),
            // @todo: test me
            endTime: new FormControl(
                this.isNew
                    ? null
                    : this.lotam?.endTime
                    ? fromTimestamp(this.lotam.endTime, this.timeZone)
                    : null
            ),
            pointOfContact: new FormControl(
                this.isNew ? null : this.lotam?.pointOfContact
            ),
            pointOfContactPhoneNumber: new FormControl(
                this.isNew ? null : this.lotam?.pointOfContactPhoneNumber
            ),
            importance: new FormControl(
                this.isNew ? 'MEDIUM' : this.lotam?.importance,
                Validators.required
            ),
            operatingRequirement: new FormControl(
                this.isNew ? null : this.lotam?.operatingRequirement,
                Validators.required
            ),
            upperLimit: new FormControl(
                this.isNew ? null : this.lotam?.upperLimit?.height
            ),
            lowerLimit: new FormControl(
                this.isNew ? null : this.lotam?.lowerLimit?.height
            )
        });
    }

    private initialiseForEdit(): Promise<NoticeAudience> {
        this.lotamId = this.lotam.id;
        this.attachmentsHandler = this.lotamService.attachmentHandler(
            this.lotam.id,
            this.organisation.id
        );

        this.locationFeatures =
            this.lotam.location?.length > 0
                ? getNoticeFeatureGroups(this.lotam.location, 1, 1).features
                : null;
        this.locationChanged$.next();

        this.missionNoticesDataService.lotams$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(notices => {
                // Filter out the notice currently being edited
                const filteredNotices = notices.filter(
                    notice => notice.id !== this.lotam.id
                );
                this.mapData$.next({
                    LOTAM: noticeLocationsToFeatureCollection(
                        filteredNotices,
                        this.organisation.timeZone
                    )
                });
            });

        combineLatest([
            this.locationChanged$,
            this.lotamForm.controls.startTime.valueChanges,
            this.lotamForm.controls.endTime.valueChanges
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([locationChanged, startTime, endTime]) => {
                if (this.locationFeatures != null) {
                    const dateRange: DateRange =
                        this.lotam.startTime != null ||
                        this.lotam.endTime != null
                            ? {
                                  start: this.lotam.startTime,
                                  end: this.lotam.endTime
                              }
                            : null;

                    this.missionNoticesDataService.refreshNotices(
                        dateRange,
                        this.organisation.id
                    );
                }
            });

        return Promise.resolve(this.lotam.audience);
    }

    private setupFormValueChanges() {
        const startTime = this.lotamForm.get('startTime');
        startTime.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(date => {
                startTime.setValue(date, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });

        const endTime = this.lotamForm.get('endTime');
        endTime.valueChanges
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(date => {
                endTime.setValue(date, {
                    onlySelf: true,
                    emitEvent: false,
                    emitModelToViewChange: true
                });
            });
    }

    private async loadLotamForClone(sourceId: number): Promise<NoticeAudience> {
        const sourceLotam = await firstValueFrom(
            this.lotamService
                .findLotamById(sourceId, this.organisation.id)
                .pipe(
                    catchError((error: FlyFreelyError) => {
                        this.logging.error(
                            error,
                            `Error while cloning LOTAM: ${error.message}`
                        );
                        return throwError(() => error);
                    }),
                    takeUntil(this.ngUnsubscribe$)
                )
        );

        const newLotam = await firstValueFrom(
            this.lotamService
                .newLotam(this.organisation.id, sourceLotam.audience)
                .pipe(takeUntil(this.ngUnsubscribe$))
        );

        this.lotamId = newLotam.id;
        this.attachmentsHandler = this.lotamService.attachmentHandler(
            this.lotamId,
            this.organisation.id
        );

        this.lotamForm.patchValue({
            subject: `${sourceLotam.subject} (Copy)`,
            description: sourceLotam.description,
            startTime: fromTimestamp(sourceLotam.startTime, this.timeZone),
            endTime: sourceLotam.endTime
                ? fromTimestamp(sourceLotam.endTime, this.timeZone)
                : null,
            pointOfContact: sourceLotam.pointOfContact,
            pointOfContactPhoneNumber: sourceLotam.pointOfContactPhoneNumber,
            importance: sourceLotam.importance,
            operatingRequirement: sourceLotam.operatingRequirement
        });

        this.locationFeatures =
            sourceLotam.location?.length > 0
                ? getNoticeFeatureGroups(sourceLotam.location, 1, 1).features
                : null;

        this.lotamForm.markAsDirty();

        return sourceLotam.audience;
    }

    private async createNewLotam(
        audience: NoticeAudience
    ): Promise<NoticeAudience> {
        const doneWorking = this.workTracker.createTracker();
        try {
            const newLotam = await firstValueFrom(
                this.lotamService
                    .newLotam(this.organisation.id, audience)
                    .pipe(takeUntil(this.ngUnsubscribe$))
            );
            this.lotamId = newLotam.id;
            this.attachmentsHandler = this.lotamService.attachmentHandler(
                this.lotamId,
                this.organisation.id
            );

            this.lotamForm.markAsDirty();
        } finally {
            doneWorking();
        }

        // locationFeatures stays null — feature groups are created only when
        // the user opens the location editor for the first time.
        return audience;
    }

    private updateLotamUtcDateRange() {
        combineLatest([
            observeFormControl(this.lotamForm.controls.startTime),
            observeFormControl(this.lotamForm.controls.endTime)
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([startTime, endTime]) => {
                const startTimeUtc =
                    startTime != null
                        ? toTimestamp(startTime, this.timeZone)
                        : '';
                const endTimeUtc =
                    endTime != null
                        ? ` - ${toTimestamp(endTime, this.timeZone)}`
                        : '';
                this.utcDateRangeString =
                    startTimeUtc.length > 0
                        ? startTimeUtc.concat(endTimeUtc)
                        : '';
                this.timeZoneLabel = moment().tz(this.timeZone).format('z');
            });
    }

    private refreshNoticeTypes(audience: NoticeAudience) {
        this.noticesService
            .findNoticeTypes({
                organisationId: this.organisation.id,
                managingOrganisationId: this.organisation.id,
                archived: false,
                audience
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: types => (this.noticeTypes = types),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while refreshing LOTAM types: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    drawNoticeArea(): void {
        // Snapshot whether we're adding or editing before the modal opens so
        // the header label is stable for the lifetime of the editor session.
        const isNewLocation = !this.hasLocation;

        // Feature groups are created lazily here — only when the editor is
        // first opened. If locationFeatures is already set (existing location),
        // pass it through unchanged.
        const featureGroupsForEditor =
            this.locationFeatures ?? getNoticeFeatureGroups([], 1, 1).features;

        const lotamDetails: SimplifiedLotamDetails | null = this.isNew
            ? null
            : {
                  lotamId: this.lotam.id,
                  dateRange:
                      this.lotamForm.controls.startTime.value != null ||
                      this.lotamForm.controls.endTime.value != null
                          ? {
                                start: this.lotamForm.controls.startTime.value,
                                end: this.lotamForm.controls.endTime.value
                            }
                          : null,
                  name: this.lotam.subject
              };

        const modal = this.modalService.show(LotamLocationEditComponent, {
            ...MODAL_OPTIONS,
            class: 'modal-lg',
            initialState: {
                organisationId: this.organisation.id,
                organisationTimeZone: this.timeZone,
                featureGroups: featureGroupsForEditor,
                lotamDetails,
                isNewLocation
            }
        });

        modal.content.featuresChange
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(locationFeatures => {
                // If the editor was saved with no features drawn, treat as no
                // location rather than storing empty feature groups.
                const hasFeatures = locationFeatures.some(
                    fg => fg.existingFeatures && fg.existingFeatures.length > 0
                );
                this.locationFeatures = hasFeatures ? locationFeatures : null;
                this.locationChanged$.next();
                this.lotamForm.markAsDirty();
            });
    }

    /**
     * Confirm with the user that we are cancelling
     *
     * @returns did the cancel succeed
     */
    tryCancel() {
        if (this.isNew) {
            return this.onCancelAdd();
        } else {
            if (this.lotamForm?.dirty) {
                return this.confirmCancel();
            } else {
                return Promise.resolve();
            }
        }
    }

    /**
     * Cancel button handler
     */
    onCancel() {
        this.tryCancel().then(() => this.cancelEdit());
    }

    /**
     *
     * @returns a promise resolving if the cancel occurred, and rejecting if not.
     */
    private onCancelAdd() {
        return this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Create LOTAM',
                'You have unsaved changes, are you sure you want to cancel?',
                'Yes',
                () =>
                    firstValueFrom(this.lotamService.deleteLotam(this.lotamId))
            )
            .then(
                () => {
                    this.lotamForm.reset();
                    this.lotamForm.markAsPristine();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error cancelling Create LOTAM: ${error.message}`
                    );
                    throw error;
                }
            );
    }

    private confirmCancel() {
        return this.commonDialoguesService.showConfirmationDialogue(
            'Confirm Cancel',
            `You have unsaved changes, are you sure you want to cancel?`,
            'Yes',
            () => Promise.resolve()
        );
    }

    cancelEdit() {
        this.editingCancelled.emit();
    }

    setNoLocation() {
        this.locationFeatures = null;
        if (!this.isNew) {
            this.locationChanged$.next();
        }
        this.lotamForm.markAsDirty();
    }

    get hasLocation() {
        // null is the canonical "no location" state. Non-null locationFeatures
        // is only set when there are actual saved features (enforced by
        // drawNoticeArea's featuresChange handler and initialiseForEdit).
        return this.locationFeatures != null;
    }

    get modalTitle() {
        return this.isNew === false ? 'Edit LOTAM' : 'Create LOTAM';
    }

    get submitButtonText() {
        return this.isNew === false ? 'Save' : 'Create';
    }

    get submitButtonTooltip() {
        return this.isNew === false ? 'Save all changes' : 'Create new LOTAM';
    }

    prepareDateTimePayload({
        startTime,
        endTime
    }): { startTime: string } | { startTime: string; endTime: string } {
        const finalPayload: { startTime: string; endTime?: string } = {
            startTime: toTimestamp(startTime, this.timeZone)
        };

        if (endTime) {
            finalPayload['endTime'] = toTimestamp(endTime, this.timeZone);
        }
        return finalPayload;
    }

    private getFeatures(): LocationFeatureInput[] {
        return (this.locationFeatures ?? []).reduce(
            (acc, fg) =>
                acc.concat(
                    fg.existingFeatures.reduce(
                        (acc2, f) =>
                            acc2.concat({
                                name: f.name,
                                geometry: f.geom,
                                type: f.categoryId,
                                properties: f.properties
                            }),
                        []
                    )
                ),
            /* fg.existingFeatures.find(
                f => f.geom.type === 'GeometryCollection'
            ) == null
                ? acc.concat(
                      fg.existingFeatures.reduce(
                          (acc2, f) =>
                              acc2.concat({
                                  name: f.name,
                                  geometry: f.geom,
                                  type: f.categoryId,
                                  properties: f.properties
                              }),
                          []
                      )
                  )
                : acc.concat(
                      fg.existingFeatures.reduce(
                          (acc2, f) =>
                              f.geom.type !== 'GeometryCollection'
                                  ? acc2.concat({
                                        name: f.name,
                                        geometry: f.geom,
                                        type: f.categoryId,
                                        properties: f.properties
                                    })
                                  : acc2.concat(
                                        (<GeometryCollection>(
                                            f.geom
                                        )).geometries.map(geom => ({
                                            name: f.name,
                                            geometry: geom,
                                            type: f.categoryId,
                                            properties: f.properties
                                        }))
                                    ),
                          []
                      )
                  ), */
            []
        );
    }

    onSave() {
        const value = this.lotamForm.value;
        const toAdd: UpdateNoticeCommand = {
            id: this.lotamId,
            subject: value.subject,
            description: value.description,
            importance: value.importance,
            pointOfContact: value.pointOfContact,
            pointOfContactPhoneNumber: value.pointOfContactPhoneNumber,
            operatingRequirement: value.operatingRequirement,
            location: this.getFeatures(),
            noticeTypeId: value.noticeType?.id,
            upperLimit: {
                height: value.upperLimit,
                units: 'FT',
                datum: 'AGL'
            },
            lowerLimit: {
                height: value.lowerLimit,
                units: 'FT',
                datum: 'AGL'
            },
            ...this.prepareDateTimePayload(value)
        };

        this.lotamService
            .updateLotam(toAdd)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: newLotam => {
                    const successMessage = this.isNew
                        ? 'Successfully create LOTAM'
                        : 'Successfully update LOTAM';
                    this.logging.success(successMessage);

                    this.update.emit(newLotam as Lotam);
                },
                error: (error: FlyFreelyError) => {
                    const errorMessage = this.isNew
                        ? `Error while add LOTAM: ${error.message}`
                        : `Error while update LOTAM: ${error.message}`;
                    this.logging.error(error, errorMessage);
                }
            })
            .add(this.workTracker.createTracker());
    }

    voidAllAcknowledgements() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Void Acknowledgements',
                `Are you sure you want to void all previous acknowledgements? This will void all existing acknowledgements of this LOTAM, marking it as unacknowledged by all users. Users that have previously acknowledged this LOTAM will have to do so again. Note: This will not void acknowledgements of Flight Operations LOTAMs on missions that have already been submitted or flown.`,
                'Yes',
                () =>
                    firstValueFrom(
                        this.noticesService
                            .voidNoticeAcknowledgements(this.lotam.id)
                            .pipe(
                                catchError((error: FlyFreelyError) => {
                                    this.logging.error(
                                        error,
                                        `Error while voiding acknowledgements: ${error.message}`
                                    );
                                    return throwError(() => error);
                                }),
                                takeUntil(this.ngUnsubscribe$)
                            )
                    )
            )
            .then(() => this.onSave())
            .catch(DO_NOTHING);
    }

    get canVoidAcknowledgement() {
        return (
            !this.isNew &&
            this.lotam &&
            (this.lotam.operatingRequirement === 'ACKNOWLEDGE' ||
                this.lotam.audience === 'FLIGHT_OPERATIONS')
        );
    }

    protected readonly LinkAction = LinkAction;
}
